import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  HelpCircle,
  PlusCircle,
  RefreshCw,
  TrendingUp,
  Wallet,
  X,
} from "lucide-react";
import { fetchLeaveBalances, fetchLeaveTypes, bulkAllocateLeave, runMonthlyAccrual, fetchLeaveBalanceTransactions } from "../../../Moduels/leave/leaveApi";
import { isLeaveBalanceType } from "./leavePackageCatalog";
import { AuthContext } from "../../../Moduels/Context/AuthContext";
import SearchableSelect from "../../common/SearchableSelect";
import {
  isEmployeeSelfService,
  resolveEmployeeIdFromProfile,
  employeeSearchSelectOptions,
} from "../../common/employeeUtils";
import { useEmployees } from "../../../hooks/useEmployees";
import { HrBtn, HrEmptyState } from "../../hr/HrDesignSystem";
import BrixigoPageLoader from "../../common/BrixigoPageLoader";
import GuidedNotice from "../../ui/GuidedNotice";
import { useLeaveOrg } from "./useLeaveOrg";
import { hasRole, R } from "../../../rbac/access";

const MONTH_NAMES = [
  "", "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function lastAccruedLabel(month, year) {
  if (!month || !year) return "Not yet accrued";
  return `${MONTH_NAMES[month]} ${year}`;
}

function fmtDays(value) {
  const n = Number(value || 0);
  return n % 1 === 0 ? n : n.toFixed(1);
}

function WalletCard({ b, orgId, onOpenHistory }) {
  const available = Number(b.available || 0);
  const opening = Number(b.opening_balance || 0);
  const accrued = Number(b.accrued || 0);
  const used = Number(b.consumed || 0);
  const carried = Number(b.carried_forward || 0);
  const lopDays = Number(b.lop_days || 0);
  const bd = b.breakdown || {};
  const currentAccrued = Number(bd.current_accrued ?? accrued);
  const cfRemain = Number(bd.carry_forward ?? carried);
  const pending = Number(bd.pending || 0);
  const expiringSoon = Number(bd.expiring_soon || 0);
  const totalEntitlement = opening + accrued;
  const pct = totalEntitlement > 0 ? Math.min(100, Math.round((available / totalEntitlement) * 100)) : 0;

  const radius = 38;
  const circumference = 2 * Math.PI * radius;
  const dash = (pct / 100) * circumference;

  const ringColor = lopDays > 0 ? "#f59e0b" : pct > 50 ? "#10b981" : pct > 20 ? "#2563eb" : "#ef4444";

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.04),0_6px_20px_-10px_rgba(15,23,42,0.08)] hover:shadow-[0_4px_12px_rgba(15,23,42,0.08)] transition-shadow overflow-hidden">
      <div className="p-5">
        <div className="flex items-center gap-4">
          <div className="relative shrink-0" style={{ width: 88, height: 88 }}>
            <svg className="-rotate-90" width="88" height="88" viewBox="0 0 88 88">
              <circle cx="44" cy="44" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="6" />
              <circle
                cx="44" cy="44" r={radius}
                fill="none"
                stroke={ringColor}
                strokeWidth="6"
                strokeDasharray={`${dash} ${circumference}`}
                strokeLinecap="round"
                style={{ transition: "stroke-dasharray 800ms cubic-bezier(0.22,1,0.36,1)" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <span className="text-[20px] font-extrabold text-slate-900 leading-none tabular-nums">
                {available % 1 === 0 ? available : available.toFixed(1)}
              </span>
              <span className="text-[10px] font-bold text-slate-400 mt-0.5">available</span>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-bold text-slate-900 leading-tight">{b.leave_type_name}</p>
            <span className="inline-block mt-1 text-[10px] font-mono font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 border border-slate-200">
              {b.leave_type_code}
            </span>
            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400">
              <Calendar size={11} />
              <span>Last accrued: <b className="text-slate-600">{lastAccruedLabel(b.last_accrued_month, b.last_accrued_year)}</b></span>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 pt-3 border-t border-slate-100">
          {[
            { label: "Current Accrued", value: currentAccrued, color: "text-emerald-600" },
            { label: "Carry Forward", value: cfRemain, color: "text-blue-600" },
            { label: "Used", value: used, color: "text-amber-600" },
            { label: "Pending", value: pending, color: "text-slate-700" },
            { label: "Expiring Soon", value: expiringSoon, color: "text-rose-600" },
            { label: "Opening", value: opening, color: "text-slate-700" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{stat.label}</p>
              <p className={`text-[14px] font-extrabold tabular-nums mt-0.5 ${stat.color}`}>
                {fmtDays(stat.value)}
              </p>
            </div>
          ))}
        </div>

        {b.has_custom_policy && (
          <p className="mt-3 text-[11px] font-semibold text-violet-700 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2">
            This employee is using a custom leave policy.
          </p>
        )}

        {lopDays > 0 && (
          <div className="mt-3 flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertTriangle size={13} className="text-amber-600 shrink-0" />
            <span className="text-amber-700 font-semibold text-[12px]">
              {lopDays.toFixed(1)} LOP day{lopDays !== 1 ? "s" : ""} incurred
            </span>
          </div>
        )}

        {orgId && (
          <button
            type="button"
            className="mt-3 w-full text-[12px] font-semibold text-indigo-600 hover:text-indigo-800"
            onClick={() => onOpenHistory?.(b)}
          >
            View leave balance history
          </button>
        )}
      </div>
    </div>
  );
}

function WalletSummary({ rows }) {
  const totalAvailable = rows.reduce((s, b) => s + Number(b.available || 0), 0);
  const totalUsed = rows.reduce((s, b) => s + Number(b.consumed || 0), 0);
  const totalAccrued = rows.reduce((s, b) => s + Number(b.accrued || 0), 0);
  const totalLOP = rows.reduce((s, b) => s + Number(b.lop_days || 0), 0);
  const totalEntitlement = totalAvailable + totalUsed;
  const utilization = totalEntitlement > 0 ? Math.round((totalUsed / totalEntitlement) * 100) : 0;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-500">Leave Wallet</p>
          <p className="text-[26px] font-extrabold text-slate-900 tabular-nums leading-none mt-1">
            {totalAvailable % 1 === 0 ? totalAvailable : totalAvailable.toFixed(1)}
            <span className="text-slate-400 text-lg ml-1">days left</span>
          </p>
        </div>
        <div className="flex items-center gap-6 text-center">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Accrued</p>
            <p className="text-[18px] font-extrabold text-emerald-600 tabular-nums">{totalAccrued.toFixed(1)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Used</p>
            <p className="text-[18px] font-extrabold text-amber-600 tabular-nums">{totalUsed.toFixed(1)}</p>
          </div>
          {totalLOP > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">LOP</p>
              <p className="text-[18px] font-extrabold text-rose-600 tabular-nums">{totalLOP.toFixed(1)}</p>
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Utilization</p>
            <p className="text-[18px] font-extrabold text-slate-700 tabular-nums">{utilization}%</p>
          </div>
        </div>
      </div>
      <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${utilization}%`,
            background: utilization > 80 ? "linear-gradient(90deg, #ef4444, #f97316)" : "linear-gradient(90deg, #2563eb, #06b6d4)",
          }}
        />
      </div>
    </div>
  );
}

function AddBalanceModal({ open, onClose, orgId, leaveTypes, employees, onSuccess }) {
  const [typeId, setTypeId] = useState("");
  const [days, setDays] = useState("");
  const [empIds, setEmpIds] = useState([]);
  const [scope, setScope] = useState("all");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const periodYear = new Date().getFullYear();

  const empOptions = useMemo(() => employeeSearchSelectOptions(employees || []), [employees]);
  const applicableTypes = (leaveTypes || []).filter((t) => !t.is_archived);

  useEffect(() => {
    if (open) { setTypeId(""); setDays(""); setEmpIds([]); setScope("all"); setErr(""); }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const daysNum = parseFloat(days);
    if (!typeId) { setErr("Select a leave type"); return; }
    if (!daysNum || daysNum <= 0) { setErr("Enter valid number of days"); return; }
    if (scope === "selected" && empIds.length === 0) { setErr("Select at least one employee"); return; }
    setBusy(true);
    setErr("");
    try {
      const res = await bulkAllocateLeave({
        organisation_id: orgId,
        leave_type_id: typeId,
        period_year: periodYear,
        days: daysNum,
        employee_ids: scope === "selected" ? empIds : [],
      });
      if (!res?.allocated) { setErr("No active employees found to allocate."); setBusy(false); return; }
      onSuccess(`Allocated ${daysNum} day(s) to ${res.allocated} employee(s) for ${periodYear}.`);
      onClose();
    } catch (ex) {
      setErr(ex.message || "Allocation failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-base font-bold text-slate-900">Add Leave Balance</h2>
            <p className="text-[12px] text-slate-500 mt-0.5">Allocate opening/additional days to employees</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/60 text-slate-400 hover:text-slate-700 transition">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-[12px] font-semibold text-slate-600 mb-1 block">Leave Type *</label>
            <select
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
              required
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-[13px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition"
            >
              <option value="">Select leave type</option>
              {applicableTypes.map((t) => (
                <option key={t.leave_type_id} value={t.leave_type_id}>
                  {t.name} ({t.code}){t.is_paid ? "" : " — Unpaid"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[12px] font-semibold text-slate-600 mb-1 block">Days to Allocate *</label>
            <input
              type="number"
              step="0.5"
              min="0.5"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="e.g. 12"
              required
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-[13px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition"
            />
          </div>

          <div>
            <label className="text-[12px] font-semibold text-slate-600 mb-1.5 block">Allocate To</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setScope("all")}
                className={`flex-1 px-3 py-2 rounded-xl text-[13px] font-semibold border transition ${scope === "all" ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              >
                All Employees
              </button>
              <button
                type="button"
                onClick={() => setScope("selected")}
                className={`flex-1 px-3 py-2 rounded-xl text-[13px] font-semibold border transition ${scope === "selected" ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              >
                Selected Employees
              </button>
            </div>
          </div>

          {scope === "selected" && (
            <div>
              <label className="text-[12px] font-semibold text-slate-600 mb-1 block">
                Employees * <span className="font-normal text-slate-400">({empIds.length} selected)</span>
              </label>
              <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-xl p-2 space-y-1 bg-slate-50">
                {empOptions.map((opt) => {
                  const checked = empIds.includes(opt.value);
                  return (
                    <label key={opt.value} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white cursor-pointer text-[13px]">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setEmpIds((prev) =>
                            checked ? prev.filter((id) => id !== opt.value) : [...prev, opt.value]
                          );
                        }}
                        className="rounded border-slate-300"
                      />
                      <span className="font-medium text-slate-800">{opt.label}</span>
                      {opt.sublabel && <span className="text-slate-400 text-[11px]">· {opt.sublabel}</span>}
                    </label>
                  );
                })}
                {empOptions.length === 0 && (
                  <p className="text-[12px] text-slate-400 text-center py-3">No employees found</p>
                )}
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
            <p className="text-[12px] text-blue-700">
              <b>Period Year:</b> {periodYear} · This will add {days || "0"} day(s) of{" "}
              {applicableTypes.find((t) => t.leave_type_id === typeId)?.name || "selected leave"} to{" "}
              {scope === "all" ? "all active employees" : `${empIds.length} selected employee(s)`}.
            </p>
          </div>

          {err && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              <AlertTriangle size={14} className="text-red-500 shrink-0" />
              <span className="text-[12px] text-red-700 font-medium">{err}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-[13px] font-semibold text-slate-700 hover:bg-slate-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-5 py-2.5 rounded-xl bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 shadow-sm transition disabled:opacity-50"
            >
              {busy ? "Allocating…" : "Allocate Balance"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

const TXN_LABELS = {
  ACCRUAL: "Monthly Accrual",
  CARRY_FORWARD: "Carry Forward",
  LEAVE_USED: "Leave Used",
  LEAVE_ADJUSTMENT: "Adjustment",
  LEAVE_EXPIRY: "Leave Expiry",
  LEAVE_ENCASHMENT: "Encashment",
  POLICY_ADJUSTMENT: "Policy Adjustment",
  REVERSAL: "Reversal",
};

/** Period-close audit rows ("carried into the next period") do not change the balance. */
function isAuditOnly(t) {
  return t.txn_type === "CARRY_FORWARD" && t.affects_balance === false;
}

/**
 * Running balance in date order. The stored running_balance reflects the moment a row was
 * written (a backfill writes all accruals first, then expiries), so it is recomputed here and
 * anchored so the last row equals the current available balance.
 */
function withRunningBalance(rows, currentAvailable) {
  const ordered = [...rows].sort((a, b) => String(a.txn_date).localeCompare(String(b.txn_date)));
  const delta = (t) => (isAuditOnly(t) ? 0 : Number(t.quantity || 0));
  const total = ordered.reduce((sum, t) => sum + delta(t), 0);
  let running = Number(currentAvailable || 0) - total;
  return ordered.map((t) => {
    running += delta(t);
    return { ...t, display_balance: Math.round(running * 100) / 100 };
  });
}

function BalanceHistoryModal({ open, onClose, orgId, balance, year }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !orgId || !balance) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchLeaveBalanceTransactions({
      organisation_id: orgId,
      employee_id: balance.employee_id,
      leave_type_id: balance.leave_type_id,
      year,
    })
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Failed to load history");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orgId, balance, year]);

  if (!open) return null;

  const displayRows = withRunningBalance(rows, balance?.available);

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-900">Leave Balance History</h2>
            <p className="text-[12px] text-slate-500 mt-0.5">{balance?.leave_type_name} · {year}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 max-h-[60vh] overflow-auto">
          {loading && <p className="text-sm text-slate-500">Loading history…</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!loading && !error && rows.length === 0 && (
            <p className="text-sm text-slate-500">No transactions recorded yet for this leave type.</p>
          )}
          {rows.length > 0 && (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="py-2">Date</th>
                  <th>Transaction</th>
                  <th className="text-right">Days</th>
                  <th className="text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((t) => (
                  <tr key={t.txn_id} className="border-t border-slate-100">
                    <td className="py-2">{t.txn_date}</td>
                    <td>
                      {TXN_LABELS[t.txn_type] || t.txn_type}
                      {isAuditOnly(t) ? " (movement)" : ""}
                    </td>
                    <td className="text-right tabular-nums font-semibold">
                      {Number(t.quantity) > 0 ? "+" : ""}
                      {t.quantity}
                    </td>
                    <td className="text-right tabular-nums">{t.display_balance.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function LvLeaveBalances() {
  const { orgId, loading: orgLoading } = useLeaveOrg();
  const { user, role, principalType } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const selfService = isEmployeeSelfService(user, role, principalType);
  const defaultEmployeeId = useMemo(() => resolveEmployeeIdFromProfile(user) || "", [user]);
  const isAdmin = hasRole(role, [R.ADMIN, R.HR]);
  const employeeIdFromUrl = searchParams.get("employee_id") || "";

  const [employeeId, setEmployeeId] = useState(employeeIdFromUrl || defaultEmployeeId);
  const [department, setDepartment] = useState("");
  const [designation, setDesignation] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [accrualBusy, setAccrualBusy] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveTypesLoaded, setLeaveTypesLoaded] = useState(false);
  const [addBalanceOpen, setAddBalanceOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [historyBalance, setHistoryBalance] = useState(null);

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const { employees, loading: empLoading } = useEmployees({ enabled: !selfService });

  const getDesig = useCallback((emp) => {
    if (!emp) return "";
    const d = emp.designation;
    if (typeof d === "string" && d) return d;
    if (d && typeof d === "object") return d.title || d.name || "";
    return emp.designation_name || "";
  }, []);

  const departmentOptions = useMemo(() => {
    const depts = new Set();
    (employees || []).forEach((emp) => {
      const d = emp.department?.name || emp.department_name;
      if (d) depts.add(d);
    });
    return [...depts].sort((a, b) => a.localeCompare(b));
  }, [employees]);

  // Designations found in the selected department (all when no department is chosen).
  const designationOptions = useMemo(() => {
    const desigs = new Set();
    (employees || []).forEach((emp) => {
      if (department && (emp.department?.name || emp.department_name || "") !== department) return;
      const d = getDesig(emp);
      if (d) desigs.add(d);
    });
    return [...desigs].sort((a, b) => a.localeCompare(b));
  }, [employees, department, getDesig]);

  const filteredEmployees = useMemo(() => {
    let list = employees || [];
    if (department) {
      list = list.filter((emp) => (emp.department?.name || emp.department_name || "") === department);
    }
    if (designation) {
      list = list.filter((emp) => getDesig(emp) === designation);
    }
    return list;
  }, [employees, department, designation, getDesig]);

  const employeeOptions = useMemo(
    () => employeeSearchSelectOptions(filteredEmployees),
    [filteredEmployees]
  );

  // Picking an employee directly fills in their Department and Designation.
  const handleEmployeeChange = (id) => {
    setEmployeeId(id);
    const emp = (employees || []).find((e) => String(e.employee_id) === String(id));
    if (!emp) return;
    const dept = emp.department?.name || emp.department_name || "";
    const desig = getDesig(emp);
    if (dept) setDepartment(dept);
    if (desig) setDesignation(desig);
  };

  useEffect(() => {
    if (orgId && isAdmin) {
      fetchLeaveTypes(orgId)
        .then(setLeaveTypes)
        .catch(() => setLeaveTypes([]))
        .finally(() => setLeaveTypesLoaded(true));
    }
  }, [orgId, isAdmin]);

  useEffect(() => {
    if (employeeIdFromUrl) {
      setEmployeeId(employeeIdFromUrl);
      return;
    }
    if (defaultEmployeeId) setEmployeeId(defaultEmployeeId);
  }, [employeeIdFromUrl, defaultEmployeeId]);

  useEffect(() => {
    if (!selfService && employeeId) {
      const stillValid = filteredEmployees.some((e) => String(e.employee_id) === String(employeeId));
      if (!stillValid) setEmployeeId("");
    }
  }, [filteredEmployees, employeeId, selfService]);

  const load = useCallback(async () => {
    if (!orgId || !employeeId) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchLeaveBalances({ organisation_id: orgId, employee_id: employeeId, year });
      setRows((data || []).filter((b) => isLeaveBalanceType(b.leave_type_code)));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [orgId, employeeId, year]);

  useEffect(() => {
    if (orgId && employeeId) load();
  }, [orgId, employeeId, year, load]);

  // UI-only example: the org has nothing to show yet (no employees, or no leave types).
  const noEmployeesYet = !selfService && !empLoading && (employees || []).length === 0;
  const noLeaveTypesYet = isAdmin && leaveTypesLoaded && leaveTypes.length === 0;
  const showBalanceExample = !loading && rows.length === 0 && (noEmployeesYet || noLeaveTypesYet);

  const handleRunAccrual = async () => {
    if (!orgId) return;
    setAccrualBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await runMonthlyAccrual({
        organisation_id: orgId,
        year: currentYear,
        month: currentMonth,
        employee_ids: employeeId ? [employeeId] : [],
      });
      setMsg(
        `Accrual complete for ${MONTH_NAMES[res.month]} ${res.year}: ` +
        `${res.accruals_created} accrual(s) posted for ${res.employees_processed} employee(s).`
      );
      await load();
    } catch (e) {
      setError(e.message || "Accrual failed");
    } finally {
      setAccrualBusy(false);
    }
  };

  const selectedEmployee = employeeOptions.find((o) => String(o.value) === String(employeeId));

  if (orgLoading) return <BrixigoPageLoader compact message="Loading…" />;

  return (
    <div className="space-y-5">
      {/* HELP BANNER — above filters */}
      {isAdmin && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200/60 overflow-hidden">
          <button
            type="button"
            onClick={() => setShowHelp((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-blue-100/40 transition"
          >
            <HelpCircle size={16} className="text-blue-600 shrink-0" />
            <span className="text-[13px] font-semibold text-blue-800">
              Click here to learn how Leave Balances work — filtering, accruals, and adding balance
            </span>
            <ChevronDown size={15} className={`ml-auto text-blue-500 transition-transform ${showHelp ? "rotate-180" : ""}`} />
          </button>
          {showHelp && (
            <div className="px-4 pb-4 pt-1 border-t border-blue-200/40">
              <ul className="space-y-2.5 text-[12.5px] text-slate-700">
                <li className="flex gap-2">
                  <span className="font-bold text-blue-700 shrink-0">1.</span>
                  <span><b>Select an Employee:</b> Use the Employee dropdown to pick an employee, then click <b className="text-blue-700">Load</b> to view their leave wallet (CL, SL, EL balances) for the selected year.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-blue-700 shrink-0">2.</span>
                  <span><b>Filter by Department / Designation:</b> Narrow the employee list by selecting a department or designation. The Employee dropdown updates automatically to show only matching employees.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-blue-700 shrink-0">3.</span>
                  <span><b>Run Monthly Accrual:</b> Click <b className="text-amber-700">Accrual</b> to post this month's leave credits (CL/SL) for the selected employee. If no employee is selected, accrual runs for all employees.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-blue-700 shrink-0">4.</span>
                  <span><b>Add Balance (bulk / department-wise):</b> Click <b className="text-emerald-700">Add Balance</b> to allocate opening or additional leave days. Choose a leave type, enter days, and select "All Employees" to allocate in bulk — or pick specific employees.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-blue-700 shrink-0">5.</span>
                  <span><b>Department-wise allocation:</b> First filter by a Department or Designation, then click <b className="text-emerald-700">Add Balance</b> and choose "All Employees" — only employees matching your current filter will receive the allocation.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-blue-700 shrink-0">6.</span>
                  <span><b>Leave Wallet Cards:</b> Each card shows available days, opening balance, accrued, used, carry-forward, and LOP. The ring chart shows utilization at a glance.</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}

      {/* FILTER BAR — single horizontal row */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div className="lv-bal-filters flex items-end gap-2" style={{ flexWrap: "nowrap", overflowX: "auto" }}>
          {!selfService && (
            <div style={{ minWidth: 140, flexShrink: 0 }}>
              <label className="lv-bal-label">Department</label>
              <select
                value={department}
                onChange={(e) => {
                  // A new department starts again from "All Designations".
                  setDepartment(e.target.value);
                  setDesignation("");
                }}
                className="w-full px-2.5 py-2 rounded-lg border border-slate-200 bg-white text-[13px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition"
              >
                <option value="">All Departments</option>
                {departmentOptions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}
          {!selfService && (
            <div style={{ minWidth: 140, flexShrink: 0 }}>
              <label className="lv-bal-label">Designation</label>
              <select
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                className="w-full px-2.5 py-2 rounded-lg border border-slate-200 bg-white text-[13px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition"
              >
                <option value="">All Designations</option>
                {designationOptions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ minWidth: 200, flex: "1 1 220px", maxWidth: 300 }}>
            {selfService ? (
              <div>
                <label className="lv-bal-label">Employee</label>
                <div className="px-2.5 py-2 rounded-lg border border-slate-200 bg-slate-50 text-[13px] font-medium text-slate-700">
                  My Balance
                </div>
              </div>
            ) : (
              <>
              <label className="lv-bal-label">
                Employee <span className="text-rose-500">*</span>
              </label>
              <SearchableSelect
                className="lv-bal-emp-select"
                value={employeeId}
                onChange={handleEmployeeChange}
                options={employeeOptions}
                loading={empLoading}
                emptyLabel="Select employee…"
                searchPlaceholder="Search by name or ID…"
                noResultsText={department || designation ? "No employees in this filter" : "No employees found"}
              />
              </>
            )}
          </div>
          <div style={{ width: 80, flexShrink: 0 }}>
            <label className="lv-bal-label">Year</label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-full px-2.5 py-2 rounded-lg border border-slate-200 bg-white text-[13px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition"
            />
          </div>
          <div className="flex items-center gap-1.5" style={{ flexShrink: 0 }}>
            <HrBtn onClick={load} variant="primary" disabled={loading || !employeeId}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Load
            </HrBtn>
            {isAdmin && (
              <button
                type="button"
                onClick={handleRunAccrual}
                disabled={accrualBusy}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-semibold text-white bg-gradient-to-r from-amber-600 to-orange-600 border border-amber-700 hover:from-amber-700 hover:to-orange-700 shadow-md hover:shadow-lg transition disabled:opacity-50"
              >
                <TrendingUp className={`w-4 h-4 ${accrualBusy ? "animate-pulse" : ""}`} />
                {accrualBusy ? "Running…" : "Accrual"}
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={() => setAddBalanceOpen(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-semibold text-white bg-gradient-to-r from-emerald-600 to-green-600 border border-emerald-700 hover:from-emerald-700 hover:to-green-700 shadow-md hover:shadow-lg transition"
              >
                <PlusCircle className="w-4 h-4" />
                Add Balance
              </button>
            )}
          </div>
        </div>

        {/* Active filters + selected employee */}
        {!selfService && (department || designation || selectedEmployee) && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
            {department && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-0.5">
                Dept: {department}
                <button type="button" onClick={() => setDepartment("")} className="ml-0.5 text-blue-400 hover:text-blue-700">×</button>
              </span>
            )}
            {designation && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2.5 py-0.5">
                Desig: {designation}
                <button type="button" onClick={() => setDesignation("")} className="ml-0.5 text-indigo-400 hover:text-indigo-700">×</button>
              </span>
            )}
            {(department || designation) && (
              <span className="text-[11px] text-slate-400 font-medium">
                {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? "s" : ""}
              </span>
            )}
            {selectedEmployee && (
              <div className="flex items-center gap-2 ml-auto">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-[10px] font-bold shadow-sm">
                  {(selectedEmployee.label || "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-[12px] font-bold text-slate-900 leading-tight">{selectedEmployee.label}</p>
                  {selectedEmployee.sublabel && (
                    <p className="text-[10px] text-slate-500">{selectedEmployee.sublabel}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ALERTS */}
      {msg && <GuidedNotice domain="leave" message={msg} variant="success" onDismiss={() => setMsg("")} />}
      {error && <GuidedNotice domain="leave" message={error} variant="error" onDismiss={() => setError("")} />}

      {/* CONTENT */}
      {showBalanceExample ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <HrEmptyState
            icon={Wallet}
            title="No leave balances yet"
            description={
              noEmployeesYet
                ? "Add employees and configure leave types — real balances will then appear here."
                : "Create a leave type (and its accrual policy) — real balances will then appear here."
            }
          />
        </div>
      ) : loading ? (
        <BrixigoPageLoader compact message="Loading balances…" />
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <HrEmptyState
            icon={Wallet}
            title="No balance records"
            description={
              !employeeId
                ? "Select an employee to view their leave wallet."
                : "No balances found for this employee and year. Run the monthly accrual to post CL/SL credits, or allocate balances from the Administration page."
            }
          />
        </div>
      ) : (
        <>
          <WalletSummary rows={rows} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map((b) => (
              <WalletCard
                key={b.balance_id}
                b={b}
                orgId={orgId}
                onOpenHistory={setHistoryBalance}
              />
            ))}
          </div>
        </>
      )}

      {isAdmin && (
        <AddBalanceModal
          open={addBalanceOpen}
          onClose={() => setAddBalanceOpen(false)}
          orgId={orgId}
          leaveTypes={leaveTypes}
          employees={filteredEmployees}
          onSuccess={(message) => {
            setMsg(message);
            load();
          }}
        />
      )}
      <BalanceHistoryModal
        open={Boolean(historyBalance)}
        onClose={() => setHistoryBalance(null)}
        orgId={orgId}
        balance={historyBalance}
        year={year}
      />
    </div>
  );
}
